import React from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "components/ui/Skeleton";
import {
  UI_CARD,
  UI_CARD_INTERACTIVE,
  UI_DASHBOARD_CARD_PAD,
  UI_STATS_GRID,
} from "lib/uiClasses";
import { formatLocaleDate } from "lib/localeFormat";

type KpiCardProps = {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  loading?: boolean;
  href?: string;
};

/**
 * Compact, tap-friendly stat card. Icon + label + value stacked in a horizontal
 * row that still fits on 360px viewports.
 */
export function KpiCard({ icon, label, value, loading, href }: KpiCardProps) {
  const content = (
    <div
      className={`flex h-full items-center gap-3 rounded-2xl p-3.5 sm:gap-4 sm:p-4 ${UI_CARD_INTERACTIVE}`}
    >
      <div className="apple-metric-icon flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-100 bg-lightPrimary dark:border-white/[0.06] dark:bg-surface-elevated sm:h-12 sm:w-12">
        <span className="text-brand-500 dark:text-brand-400">{icon}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-gray-500 dark:text-slate-400">{label}</p>
        {loading ? (
          <Skeleton className="mt-1.5 h-6 w-14 sm:h-7 sm:w-16" />
        ) : (
          <p className="mt-0.5 truncate text-xl font-bold text-navy-700 dark:text-slate-50 sm:text-2xl">
            {value}
          </p>
        )}
      </div>
    </div>
  );

  if (href) {
    return (
      <Link to={href} className="block h-full">
        {content}
      </Link>
    );
  }

  return content;
}

/**
 * Responsive KPI grid: 1 col mobile → 2 cols sm/md → 3 lg → 4 xl.
 */
export function KpiGrid({ children }: { children: React.ReactNode }) {
  return <div className={UI_STATS_GRID}>{children}</div>;
}

export function DashboardSection({
  title,
  subtitle,
  children,
  action,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section className={`${UI_DASHBOARD_CARD_PAD} ${UI_CARD}`}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-bold text-navy-700 dark:text-slate-50 sm:text-lg">
            {title}
          </h2>
          {subtitle ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-slate-400 sm:text-sm">{subtitle}</p>
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function DashboardErrorState({
  message,
  onRetry,
  retryLabel,
}: {
  message: string;
  onRetry: () => void;
  retryLabel: string;
}) {
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-6 text-center dark:border-red-500/25 dark:bg-red-500/10">
      <p className="text-sm text-red-700 dark:text-red-200">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
      >
        {retryLabel}
      </button>
    </div>
  );
}

/**
 * Empty-state card for dashboard sections. Mobile-friendly with icon + copy.
 */
export function DashboardEmptyState({
  icon,
  title,
  description,
}: {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-4 py-6 text-center dark:border-white/10 dark:bg-white/[0.02]">
      {icon ? (
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-brand-500 shadow-sm dark:bg-input dark:text-brand-400">
          {icon}
        </div>
      ) : null}
      <p className="text-sm font-semibold text-gray-700 dark:text-slate-200">{title}</p>
      {description ? (
        <p className="text-xs text-gray-500 dark:text-slate-400">{description}</p>
      ) : null}
    </div>
  );
}

export function formatDashboardDate(value: string | null | undefined, locale: string): string {
  if (!value) return "—";
  return formatLocaleDate(value, locale === "ar", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
